// src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

function Dashboard({ onLogout, navigateTo, currentView }) {
  const [dashboardData, setDashboardData] = useState({
    summaryByMarketplaceAndStatus: [],
    statusPercentages: [],
    totalOrders: 0,
    totalSales: '0.00',
    ordersRecibido: 0,
    ordersInventarioPendiente: 0,
    ordersEnviada: 0,
    ordersCancelada: 0,
    recentHistory: [],
    loading: true,
    error: null
  });

  const [dateFilter, setDateFilter] = useState('Hoy');
  const [dashboardFromDate, setDashboardFromDate] = useState('');
  const [dashboardToDate, setDashboardToDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Estados para Demoras ML
  const [delayedOrders, setDelayedOrders] = useState([]);
  const [loadingDelayed, setLoadingDelayed] = useState(false);

  const COLORS = ['#0088FE', '#FF8042', '#00C49F', '#FFBB28', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c', '#d06262'];
  const { user, token } = useAuth();

  const calculateDates = useCallback((filter) => {
    const today = new Date();
    let from = new Date();
    let to = new Date();

    switch (filter) {
      case 'Hoy':
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'Ultima Semana':
        from.setDate(today.getDate() - 7);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'Ultimos 30 Dias':
      default:
        from.setDate(today.getDate() - 30);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'Personalizado':
        if (dashboardFromDate && dashboardToDate) {
          from = new Date(dashboardFromDate);
          to = new Date(dashboardToDate);
          to.setHours(23, 59, 59, 999);
        } else {
          from.setDate(today.getDate() - 30);
          from.setHours(0, 0, 0, 0);
        }
        break;
    }
    return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
  }, [dashboardFromDate, dashboardToDate]);

  useEffect(() => {
    if (dateFilter !== 'Personalizado') {
      const { from, to } = calculateDates(dateFilter);
      setDashboardFromDate(from);
      setDashboardToDate(to);
    }
  }, [dateFilter, calculateDates]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) {
        setDashboardData(prev => ({ ...prev, loading: false, error: 'No autenticado. Por favor, inicia sesión.' }));
        return;
      }

      setDashboardData(prev => ({ ...prev, loading: true, error: null }));
      try {
        const queryParams = new URLSearchParams();
        if (dashboardFromDate) queryParams.append('fromDate', dashboardFromDate);
        if (dashboardToDate) queryParams.append('toDate', dashboardToDate);

        const summaryResponse = await fetch(`/api/orders/dashboard-summary?${queryParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const summaryData = await summaryResponse.json();

        let totalSales = '0.00';
        if (user?.rol === 'admin') {
          const salesResponse = await fetch(`/api/orders/total-sales?${queryParams.toString()}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (salesResponse.ok) {
            const salesData = await salesResponse.json();
            totalSales = salesData.totalSales;
          }
        }

        const historyResponse = await fetch(`/api/orders/recent-history?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const historyData = await historyResponse.json();
        const recentHistory = historyResponse.ok ? historyData.history : [];

        if (summaryResponse.ok) {
          const calculatedStatusPercentages = summaryData.statusPercentages.map(item => ({
            ...item,
            percent: summaryData.totalOrders > 0 ? item.value / summaryData.totalOrders : 0
          }));

          const ordersRecibido = summaryData.statusPercentages.find(s => s.name === 'Recibido')?.value || 0;
          const ordersInventarioPendiente = summaryData.statusPercentages.find(s => s.name === 'Inventario Pendiente')?.value || 0;
          const ordersEnviada = summaryData.statusPercentages.find(s => s.name === 'Enviada')?.value || 0;
          const ordersCancelada = summaryData.statusPercentages.find(s => s.name === 'Cancelada')?.value || 0;

          setDashboardData({
            summaryByMarketplaceAndStatus: summaryData.summaryByMarketplaceAndStatus,
            statusPercentages: calculatedStatusPercentages,
            totalOrders: summaryData.totalOrders,
            totalSales: totalSales,
            ordersRecibido,
            ordersInventarioPendiente,
            ordersEnviada,
            ordersCancelada,
            recentHistory,
            loading: false,
            error: null
          });
        } else {
          setDashboardData(prev => ({ ...prev, error: summaryData.mensaje || 'Error al cargar datos.', loading: false }));
        }
      } catch (err) {
        setDashboardData(prev => ({ ...prev, error: 'Error de conexión con el servidor.', loading: false }));
      }
    };

    if (dashboardFromDate && dashboardToDate) {
      fetchDashboardData();
    }
  }, [dashboardFromDate, dashboardToDate, token, user]);

  useEffect(() => {
    const fetchDelayedOrders = async () => {
      if (!token) return;
      setLoadingDelayed(true);
      try {
        const response = await fetch('/api/meli/orders/delayed', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setDelayedOrders(data.data);
        }
      } catch (e) {
        console.error('Error fetching delayed orders:', e);
      }
      setLoadingDelayed(false);
    };

    fetchDelayedOrders();
  }, [token]);

  const allStatuses = [
    'Recibido',
    'Inventario Pendiente',
    'Confirmada',
    'Preparación',
    'Listo para envío',
    'Enviada',
    'Cancelada',
    'Pendiente de factura'
  ];

  const marketplaces = [...new Set(dashboardData.summaryByMarketplaceAndStatus.map(item => item.canal_origen))].sort();
  const grandTotalsByStatus = allStatuses.reduce((acc, status) => {
    acc[status] = dashboardData.summaryByMarketplaceAndStatus.reduce((sum, item) => sum + (item.status_counts[status] || 0), 0);
    return acc;
  }, {});

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' });
  };

  return (
    <>
      {/* ── Título y filtro ─────────────────────────────────────────── */}
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Panel de Control</h2>
          <p className="text-sm text-gray-500 mt-0.5">Resumen general de operaciones y ventas</p>
        </div>
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setShowDatePicker(e.target.value === 'Personalizado');
            }}
            className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 text-sm"
          >
            <option>Hoy</option>
            <option>Ultima Semana</option>
            <option>Ultimos 30 Dias</option>
            <option>Personalizado</option>
          </select>
          {showDatePicker && (
            <>
              <input type="date" value={dashboardFromDate} onChange={(e) => setDashboardFromDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input type="date" value={dashboardToDate} onChange={(e) => setDashboardToDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </>
          )}
        </div>
      </div>

      {dashboardData.loading ? (
        <div className="text-center text-gray-500 text-base py-12">Cargando datos...</div>
      ) : dashboardData.error ? (
        <div className="text-center text-red-600 text-base py-12">Error: {dashboardData.error}</div>
      ) : (
        <>
          {/* ── Fila superior: Total pedidos + Ingresos ──────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">

            {/* Total de Pedidos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Total de Pedidos</p>
                <p className="text-5xl font-bold text-gray-800">{dashboardData.totalOrders}</p>
              </div>
              <div className="text-gray-200">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16">
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>

            {/* Ingresos Totales — solo admin */}
            {user?.rol === 'admin' ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Ingresos Totales</p>
                  <p className="text-4xl font-bold text-green-600">
                    ${parseFloat(dashboardData.totalSales).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-green-100">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
              </div>
            ) : (
              /* Placeholder para no-admin: Enviados destacado */
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Pedidos Enviados</p>
                  <p className="text-5xl font-bold text-teal-600">{dashboardData.ordersEnviada}</p>
                </div>
                <div className="text-teal-100">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Panel de Órdenes en Demora (Mercado Libre) - Solo visible para Logística/Admin (o quienes ven el Dashboard) */}
          <div className="bg-red-50 p-6 rounded-xl shadow-sm border border-red-200 mb-6 border-l-4 border-l-red-500">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-red-800">
                🚨 Monitoreo de Demoras en Mercado Libre
              </h3>
            </div>
            
            <p className="text-sm text-red-600 mb-4">
              Pedidos pendientes que llevan <strong>más de 24 horas</strong> sin despacharse (posibles demoras o alertas logísticas).
            </p>

            {loadingDelayed ? (
              <p className="text-red-500">Actualizando demoras...</p>
            ) : delayedOrders.length === 0 ? (
              <p className="text-green-700 font-medium">¡Excelente! No hay pedidos en demora en este momento.</p>
            ) : (
              <div className="overflow-x-auto bg-white rounded shadow-sm">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Orden ID / Pack ID</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Guía de Envío</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Artículos</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Comprador</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Fecha Creación</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-red-800 uppercase">Tiempo Transcurrido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {delayedOrders.map(order => (
                      <tr key={order.id} className="hover:bg-red-50 transition-colors">
                        <td className="px-4 py-2 text-sm font-medium text-red-900">
                          {order.id} {order.pack_id && <span className="text-xs bg-red-200 px-1 rounded ml-2">PACK: {order.pack_id}</span>}
                        </td>
                        <td className="px-4 py-2 text-sm font-semibold text-gray-800">
                          {order.carrier} <br/>
                          <span className="text-blue-600 tracking-wider">{order.tracking_number || 'Sin guía aún'}</span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-800 max-w-xs break-words">
                          {order.items}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-800">{order.buyer_nickname}</td>
                        <td className="px-4 py-2 text-sm text-gray-800">{new Date(order.date_created).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm font-bold text-red-600">{order.hours_since_created} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Fila de 4 KPIs secundarios ───────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

            {/* Recibidos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Recibidos</p>
                <p className="text-2xl font-bold text-gray-800 mt-0.5">{dashboardData.ordersRecibido}</p>
              </div>
            </div>

            {/* Inv. Pendiente */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Inv. Pendiente</p>
                <p className="text-2xl font-bold text-gray-800 mt-0.5">{dashboardData.ordersInventarioPendiente}</p>
              </div>
            </div>

            {/* Enviados */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Enviados</p>
                <p className="text-2xl font-bold text-gray-800 mt-0.5">{dashboardData.ordersEnviada}</p>
              </div>
            </div>

            {/* Cancelados */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-400 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Cancelados</p>
                <p className="text-2xl font-bold text-gray-800 mt-0.5">{dashboardData.ordersCancelada}</p>
              </div>
            </div>
          </div>

          {/* ── Tabla + Gráfica ───────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">

            {/* Tabla marketplaces */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="px-5 py-4 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-700">Pedidos por Marketplace y Estatus</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Marketplace</th>
                      {allStatuses.map(status => (
                        <th key={status} className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {status.replace('Inventario Pendiente', 'Inventario\nPendiente')}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {marketplaces.length === 0 ? (
                      <tr>
                        <td colSpan={allStatuses.length + 2} className="px-4 py-8 text-center text-gray-400 text-sm">
                          Sin datos para el período seleccionado
                        </td>
                      </tr>
                    ) : marketplaces.map(marketplace => (
                      <tr key={marketplace} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">{marketplace}</td>
                        {allStatuses.map(status => {
                          const item = dashboardData.summaryByMarketplaceAndStatus.find(d => d.canal_origen === marketplace);
                          const val = item?.status_counts[status] || 0;
                          return (
                            <td key={`${marketplace}-${status}`} className="px-3 py-3 text-center text-sm text-gray-600">
                              {formatNumber(val)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center text-sm font-bold text-blue-600">
                          {formatNumber(dashboardData.summaryByMarketplaceAndStatus.find(d => d.canal_origen === marketplace)?.total_marketplace_orders || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {marketplaces.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-4 py-3 text-sm text-gray-700">Total General</td>
                        {allStatuses.map(status => (
                          <td key={`total-${status}`} className="px-3 py-3 text-center text-sm font-bold text-blue-700">
                            {formatNumber(grandTotalsByStatus[status])}
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center text-sm font-bold text-blue-700">
                          {formatNumber(dashboardData.totalOrders)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* Gráfica de Pie */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col">
              <div className="px-5 py-4 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-700">Pedidos por Estatus</h4>
              </div>
              <div className="flex-1 flex items-center justify-center p-4">
                {dashboardData.statusPercentages.length > 0 && dashboardData.totalOrders > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={dashboardData.statusPercentages}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                      >
                        {dashboardData.statusPercentages.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} pedidos`, name]} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-sm text-center">No hay datos para mostrar.</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Actividad Reciente ────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Actividad Reciente</h3>
            </div>
            {dashboardData.recentHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha/Hora</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Pedido Ref.</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Evento</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Descripción</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Por</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dashboardData.recentHistory.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{formatDateTime(item.timestamp)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">{item.referencia_plataforma}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.event_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{item.description}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.performed_by_username}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">No hay actividad reciente.</p>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default Dashboard;
